import { create } from "zustand";
import useResponseManagerV3FiltersStore, {
  waitForFiltersHydration
} from "./useResponseManagerV3FiltersStore";
import useResponseManagerV3QuickFiltersStore from "./useResponseManagerV3QuickFiltersStore";
import ResponseManagerV3Service from "../../services/ResponseManagerV3/ResponseManagerV3Service";
import {
  CandidateProfile,
  CandidatesResponse,
  CMPTab,
  TabsResponse
} from "../../components/ResponseManagerV3/types";
import { getQueryClient } from "../../services/queryClientService";

// Type alias for convenience
type Candidate = CandidateProfile;

interface TabsData {
  tabs: CMPTab[];
  fetchedState: "LOADING" | "SUCCESS" | "ERROR" | null;
}

// Job data type based on API response
export interface JobData {
  job_id: number;
  job_title: string;
  job_contact: string;
  status: string;
  jobPlan: string;
  company_name: string;
  boost_score: string;
  address: string;
  job_status: string;
  boost_details: {
    status: string;
    next_boost_status: string;
    job_mini_boost_start_date: string;
    current_mini_boost_count: number;
    has_mini_boost: boolean;
    total_mini_boost_limit: number;
    current_mini_boost_count_via_crm: number;
    mini_boost_credits_worth_done_by_crm: number | null;
  };
  is_resdex_allowed: boolean;
  is_download_enabled: boolean;
  per_job_download_limit: number;
  free_count_left: number;
  free_count_used: number;
  paid_count_used: number;
  per_job_bulk_unlock_limit: number;
  per_day_bulk_unlock_limit_breached: boolean;
  salary: string;
  job_address: string;
  category_id: number;
}

interface ResponseManagerV3PageStore {
  candidatesData: Candidate[];
  fetchedCandidatesState: "LOADING" | "SUCCESS" | "ERROR" | null;
  jobData: JobData | null;
  tabsData: TabsData;
  totalCount: number;
  totalUserCount: number;
  currentPage: number;
  totalPages: number;
  isInitializing: boolean;
  lastRequestBody: any | null;
  freeUnlockCountLeft: number;

  // Actions
  initializePageData: (jobId: number, tabId?: string) => Promise<void>;
  fetchTabs: (jobId: number) => Promise<TabsResponse | null>;
  fetchResponseManagerV3FiltersData: (
    jobId: number,
    tabId?: string
  ) => Promise<void>;
  fetchJobDetails: (jobId: number) => Promise<void>;
  fetchResponseManagerV3CandidatesData: (
    requestBody: any
  ) => Promise<CandidatesResponse>;
  setCandidatesData: (candidates: Candidate[]) => void;
  setFetchedCandidatesState: (
    state: "LOADING" | "SUCCESS" | "ERROR" | null
  ) => void;
  setJobData: (data: JobData) => void;
  setTabsData: (data: TabsData) => void;
  setPagination: (page: number, totalPages: number, totalCount: number) => void;
  updateCandidateHiredStatus: (candidateId: number, isHired: boolean) => void;
  updateCandidateRemovedStatus: (
    candidateId: number,
    isRemoved: boolean
  ) => void;
  deleteCandidateFromList: (candidateId: number) => void;
  decrementFreeUnlockCount: () => void;
  incrementPaidUnlockCount: () => void;
  updateTabCount: (tabId: string, delta: number) => void;
  resetStore: () => void;
}

const useResponseManagerV3PageStore = create<ResponseManagerV3PageStore>(
  (set, get) => ({
    candidatesData: [],
    fetchedCandidatesState: null,
    jobData: null,
    tabsData: {
      tabs: [],
      fetchedState: null
    },
    totalCount: 0,
    currentPage: 1,
    totalPages: 1,
    isInitializing: false,
    lastRequestBody: null,
    freeUnlockCountLeft: 0,
    totalUserCount: 0,
    // Optimized initialization method - Updated to use CMP V1 APIs
    initializePageData: async (jobId: number, tabId?: string) => {
      try {
        set({ isInitializing: true });

        const filtersStore = useResponseManagerV3FiltersStore.getState();
        filtersStore.setFetchedState("LOADING");
        filtersStore.setCurrentJobAndTab(jobId, tabId);

        // CRITICAL: Wait for hydration to complete before reading persisted values
        // This ensures sessionStorage values are loaded before we check them
        await waitForFiltersHydration();

        // Now safely read persisted values after hydration
        const hydratedState = useResponseManagerV3FiltersStore.getState();
        const persistedCurrentFilters = hydratedState.currentFilters;
        const persistedJobId = hydratedState.lastJobId;
        const persistedFormData = hydratedState.defaultFormData;

        // Check if we have valid persisted filters for this job
        const hasPersistedFilters =
          persistedCurrentFilters &&
          persistedCurrentFilters.length > 0 &&
          persistedJobId === jobId;

        // Use persisted filters if available, otherwise use empty array
        const filtersToSend = hasPersistedFilters
          ? persistedCurrentFilters
          : [];

        // Step 1: Call tabs, filters and job details APIs in parallel
        // Use React Query to fetch job details (will use cache if available)
        const queryClient = getQueryClient();

        const [tabsResponse, filtersResponse, jobDetailsResponse] =
          await Promise.all([
            ResponseManagerV3Service.getTabs(jobId),
            ResponseManagerV3Service.getCMPFilters(jobId, tabId, filtersToSend),
            queryClient.fetchQuery({
              queryKey: ["jobDetailsV2", jobId],
              queryFn: async () => {
                const response = await ResponseManagerV3Service.getJobDetails(
                  jobId
                );
                return (response as any).data;
              }
            })
          ]);

        // Step 2: Update tabs data
        if (tabsResponse?.status && tabsResponse.data) {
          set({
            tabsData: {
              tabs: tabsResponse.data.tabs || [],
              fetchedState: "SUCCESS"
            }
          });
        } else {
          console.error("[ResponseManagerV3] Failed to get tabs response");
        }

        // Step 3: Update filters store with fetched data
        if (filtersResponse) {
          // Always update metadata from API (contains latest counts)
          filtersStore.setFiltersMetadata({
            sections: filtersResponse?.sections || []
          });
          filtersStore.setFetchedState("SUCCESS");

          if (hasPersistedFilters) {
            // Use persisted filter VALUES from store, metadata from API
            // This ensures filter counts are updated but values remain from user's session
            filtersStore.updateDefaultFilters(persistedCurrentFilters);
            filtersStore.setDynamicFilters(persistedFormData);
            filtersStore.setDynamicBaseline(persistedFormData);
            filtersStore.setLastJobId(jobId);
          } else {
            // First time or job changed - use filters from API response
            const defaultFilters =
              filtersResponse?.sections?.map(section => ({
                section: section.section,
                filters: section.filters.map(filter => ({
                  key: filter.key,
                  selectedValue: filter.defaultValue || null
                }))
              })) || [];

            const defaultFormData: any = {};
            filtersResponse?.sections?.forEach(section => {
              section.filters.forEach(filter => {
                defaultFormData[filter.key] = filter.defaultValue ?? null;
              });
            });

            filtersStore.updateDefaultFilters(defaultFilters);
            filtersStore.setInitialFilters(defaultFilters);
            filtersStore.updateDefaultFormFilters(defaultFormData);
            filtersStore.setDynamicFilters(defaultFormData);
            filtersStore.setDynamicBaseline(defaultFormData);
            filtersStore.setLastJobId(jobId);
          }
        }

        // jobDetailsResponse is already the data object (extracted in queryFn)
        if (jobDetailsResponse) {
          set({
            jobData: jobDetailsResponse as JobData,
            freeUnlockCountLeft:
              (jobDetailsResponse as JobData).free_count_left || 0
          });
        }

        set({ isInitializing: false });
      } catch (error) {
        set({ isInitializing: false });
        const filtersStore = useResponseManagerV3FiltersStore.getState();
        filtersStore.setFetchedState("ERROR");
      }
    },

    // Fetch tabs - NEW CMP V1 API
    fetchTabs: async (jobId: number) => {
      try {
        set(state => ({
          tabsData: {
            ...state.tabsData,
            fetchedState: "LOADING"
          }
        }));

        const response = await ResponseManagerV3Service.getTabs(jobId);

        if (response?.status && response.data) {
          set({
            tabsData: {
              tabs: response.data.tabs || [],
              fetchedState: "SUCCESS"
            }
          });
          return response;
        }

        set(state => ({
          tabsData: {
            ...state.tabsData,
            fetchedState: "ERROR"
          }
        }));
        return null;
      } catch (error) {
        console.error("Error fetching tabs:", error);
        set(state => ({
          tabsData: {
            ...state.tabsData,
            fetchedState: "ERROR"
          }
        }));
        return null;
      }
    },

    fetchResponseManagerV3FiltersData: async (
      jobId: number,
      tabId?: string
    ) => {
      try {
        const filtersStore = useResponseManagerV3FiltersStore.getState();
        filtersStore.setFetchedState("LOADING");

        // Get current filters to pass to API
        const currentFilters = filtersStore.currentFilters || [];
        const filtersToSend = currentFilters.length > 0 ? currentFilters : [];

        // Step 1: Fetch filters using CMP V1 API
        const filtersResponse = await ResponseManagerV3Service.getCMPFilters(
          jobId,
          tabId,
          filtersToSend
        );

        if (filtersResponse) {
          // Set filters metadata and initial filters
          filtersStore.setFiltersMetadata({
            sections: filtersResponse.sections || []
          });
          filtersStore.setFetchedState("SUCCESS");

          // Extract default filters from the response
          const defaultFilters =
            filtersResponse.sections?.map(section => ({
              section: section.section,
              filters: section.filters.map(filter => ({
                key: filter.key,
                selectedValue: filter.selectedValue || null
              }))
            })) || [];

          // Keyed object for form defaults
          const defaultFormData: any = {};
          filtersResponse.sections?.forEach(section => {
            section.filters.forEach(filter => {
              defaultFormData[filter.key] = filter.selectedValue ?? null;
            });
          });

          filtersStore.updateDefaultFilters(defaultFilters);
          filtersStore.setInitialFilters(defaultFilters);
          filtersStore.updateDefaultFormFilters(defaultFormData);
        }
      } catch (error) {
        console.error("Error fetching filters data:", error);

        const filtersStore = useResponseManagerV3FiltersStore.getState();
        filtersStore.setFetchedState("ERROR");
      }
    },

    fetchJobDetails: async (jobId: number) => {
      try {
        const response = await ResponseManagerV3Service.getJobDetails(jobId);

        const { data = {} } = response;

        if (data) {
          set({
            jobData: data as JobData,
            freeUnlockCountLeft: (data as JobData).free_count_left || 0
          });
        }
      } catch (error) {
        console.error("Error fetching job details:", error);
        // Don't set error state for this non-critical data
      }
    },

    fetchResponseManagerV3CandidatesData: async (requestBody: any) => {
      try {
        set({
          fetchedCandidatesState: "LOADING",
          lastRequestBody: requestBody // Store for refetching
        });

        // Extract job_id and tab_id from requestBody
        const jobId = requestBody.job_id;
        const tabId = requestBody.tab_id;

        // Call CMP V1 candidates API
        const response = await ResponseManagerV3Service.getCMPCandidates(
          jobId,
          tabId,
          requestBody
        );

        if (response?.status) {
          set({
            candidatesData: response.data.candidates || [],
            totalUserCount: response.data.totalUserCount || 0,
            totalCount: response.data.totalCount || 0,
            currentPage: response.data.page || 1,
            totalPages: Math.ceil(
              (response.data.totalCount || 0) / (response.data.size || 50)
            ),
            fetchedCandidatesState: "SUCCESS"
          });
        } else {
          set({ fetchedCandidatesState: "ERROR" });
        }

        return response as any as CandidatesResponse;
      } catch (error) {
        console.error("Error fetching candidates:", error);
        set({ fetchedCandidatesState: "ERROR" });
        throw error;
      }
    },

    setCandidatesData: (candidates: Candidate[]) => {
      set({ candidatesData: candidates });
    },

    setFetchedCandidatesState: (
      state: "LOADING" | "SUCCESS" | "ERROR" | null
    ) => {
      set({ fetchedCandidatesState: state });
    },

    setJobData: (data: JobData) => {
      set({ jobData: data });
    },

    setTabsData: (data: TabsData) => {
      set({ tabsData: data });
    },

    setPagination: (page: number, totalPages: number, totalCount: number) => {
      set({
        currentPage: page,
        totalPages: totalPages,
        totalCount: totalCount
      });
    },

    updateCandidateHiredStatus: (candidateId: number, isHired: boolean) => {
      set(state => ({
        candidatesData: state.candidatesData.map(candidate =>
          candidate.user_id === candidateId
            ? { ...candidate, is_hired: isHired }
            : candidate
        )
      }));
    },

    deleteCandidateFromList: (candidateId: number) => {
      set(state => ({
        candidatesData: state.candidatesData.filter(
          candidate => candidate.user_id !== candidateId
        )
      }));
    },

    // Decrement free unlock count when a free unlock is used
    decrementFreeUnlockCount: () => {
      set(state => ({
        jobData: state.jobData
          ? {
              ...state.jobData,
              free_count_left: Math.max(0, state.jobData.free_count_left - 1),
              free_count_used: state.jobData.free_count_used + 1
            }
          : state.jobData,
        freeUnlockCountLeft: Math.max(0, state.freeUnlockCountLeft - 1)
      }));
    },

    // Increment paid unlock count when a paid unlock is used
    incrementPaidUnlockCount: () => {
      set(state => ({
        jobData: state.jobData
          ? {
              ...state.jobData,
              paid_count_used: state.jobData.paid_count_used + 1
            }
          : state.jobData
      }));
    },

    updateCandidateRemovedStatus: (candidateId: number, isRemoved: boolean) => {
      set(state => ({
        candidatesData: state.candidatesData.map(candidate =>
          candidate.user_id === candidateId
            ? { ...candidate, is_removed: isRemoved }
            : candidate
        )
      }));
    },

    // Update tab count by delta (can be positive or negative)
    updateTabCount: (tabId: string, delta: number) => {
      set(state => ({
        tabsData: {
          ...state.tabsData,
          tabs: state.tabsData.tabs.map(tab =>
            tab.tab_id === tabId
              ? { ...tab, count: Math.max(0, tab.count + delta) }
              : tab
          )
        }
      }));
    },

    resetStore: () => {
      set({
        candidatesData: [],
        fetchedCandidatesState: null,
        jobData: null,
        tabsData: {
          tabs: [],
          fetchedState: null
        },
        totalCount: 0,
        currentPage: 1,
        totalPages: 1,
        isInitializing: false,
        freeUnlockCountLeft: 0
      });

      // NOTE: Do NOT reset filters here!
      // Filter persistence is handled in initializePageData which:
      // 1. Waits for hydration to complete
      // 2. Checks if persisted filters exist for this job
      // 3. Uses persisted filters or API defaults accordingly
      // Resetting filters here would clear persisted values before hydration check
    }
  })
);

export default useResponseManagerV3PageStore;
